import { useEffect } from 'react'
import { useInterpret, useSelector } from '@xstate/react'
import { Box, Link, Typography } from '@mui/material'
import { BaseDialog } from 'src/components/Dialogs/Base/BaseDialog'
import { CDN_PATH, PAYMENT_PROCESSORS } from 'src/constants/global'
import lmnMachine from 'src/machines/lmnMachine'
import { getLmnMerchantName, getLmnMerchantProcessor } from 'src/helpers/lmnHelpers'
import LmnButton from 'src/components/Button/LmnButton'
import styles from './DiscoveryDialog.scss'

interface DiscoveryLmnDialogProps {
  merchantUuid: string
  isOpen: boolean
  onClose: () => void
}

const DiscoveryLmnDialog = ({
  merchantUuid,
  isOpen,
  onClose,
}: DiscoveryLmnDialogProps): JSX.Element => {
  const lmnContextService = useInterpret(lmnMachine)
  const { send } = lmnContextService
  const merchantName = useSelector(lmnContextService, getLmnMerchantName)
  const merchantProcessor = useSelector(lmnContextService, getLmnMerchantProcessor)
  const marketplaceUuids = [
    'af44c0d3-b78e-4388-9570-e41277b5130c', // production
    '96004ffe-a4ca-4594-8a02-ae6e04456c74', // staging
  ]
  const lmnhHref = 'https://partners.sikahealth.com/lmn'

  // marketplaceUuids.includes(merchantUuid)
  //   ? 'https://partners.sikahealth.com/lmn'
  //   : 'https://www.sikahealth.com/lmn'
  const handleCloseLmnDialog = (): void => {
    if (typeof onClose === 'function') {
      onClose()
    }
  }

  useEffect(() => {
    if (isOpen) {
      send({
        type: 'ON_LOAD',
        data: {
          merchantUuid,
          onCancel: onClose,
        },
      })
    }
  }, [merchantUuid, isOpen, send, onClose])

  return (
    <BaseDialog isDiscoveryDialog isOpen={isOpen} onClose={onClose}>
      <Box className={styles.discoveryDialogContainer}>
        <Typography variant="h3Bold" color="primary.main" lineHeight="1.3">
          Spend smart with your HSA or FSA
        </Typography>
        {!marketplaceUuids.includes(merchantUuid) && (
          <Typography variant="body3" color="primary.main" marginTop="16px">
            {merchantName}
            {' '}
            is partnering with Sika so you can pay with your HSA or FSA.
          </Typography>
        )}
        <Typography variant="body3" color="primary.main" marginTop="12px">
          All you need is a
          <Link
            marginLeft="4px"
            href={lmnhHref}
            color="primary.main"
            variant="inherit"
            target="_blank"
            fontWeight="bold"
          >
            Letter of Medical Necessity (LMN)
          </Link>
          , and we can help with that.
        </Typography>

        {/* { merchantProcessor === PAYMENT_PROCESSORS.STRIPE && (
          <Box className={styles.discoveryDialogLmnButtonContainer}>
            <LmnButton
              merchantUuid={merchantUuid}
              onCancel={handleCloseLmnDialog}
              buttonFullWidth
            />
          </Box>
        )} */}
        {merchantProcessor === PAYMENT_PROCESSORS.STRIPE ? (
          <>
            <Typography variant="body3Bold" color="primary.main" marginTop="16px">
              Here&apos;s how:
            </Typography>
            <Box className={styles.discoveryDialogItemRowLmn}>
              <img
                className={styles.discoveryDialogItemIconLmn}
                src={`${CDN_PATH}/images/step-one-green.svg`}
                alt="Step One"
              />
              <Box>
                <Typography variant="body3Bold" color="primary.main">
                  See if You qualify for an LMN
                </Typography>
                <Typography variant="body4" color="gray1.main">
                  Chat with a certified healthcare provider through Sika to evaluate
                  if you qualify for an LMN.
                </Typography>
              </Box>
            </Box>
            <Box className={styles.discoveryDialogItemRowLmn}>
              <img
                className={styles.discoveryDialogItemIconLmn}
                src={`${CDN_PATH}/images/step-two-green.svg`}
                alt="Step One"
              />
              <Box>
                <Typography variant="body3Bold" color="primary.main">
                  Choose Sika to checkout with your HSA/FSA
                </Typography>
                <Typography variant="body4" color="gray1.main">
                  Once you have received an LMN, choose Sika at checkout and make
                  your next
                  {' '}
                  {merchantName}
                  {' '}
                  purchase!
                </Typography>
              </Box>
            </Box>
            <Box className={styles.discoveryDialogItemRowLmn}>
              <img
                className={styles.discoveryDialogItemIconLmn}
                src={`${CDN_PATH}/images/step-three-green.svg`}
                alt="Step One"
              />
              <Box>
                <Typography variant="body3Bold" color="primary.main">
                  Spend smart with your pre-tax dollars
                </Typography>
                <Typography variant="body4" color="gray1.main">
                  Pay with your HSA/FSA through Sika, and we&apos;ll email you a
                  receipt and instructions for submission to your administrator.
                </Typography>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body3Bold" color="primary.main" marginTop="16px">
              Here&apos;s how:
            </Typography>
            <Box className={styles.discoveryDialogItemRowLmn}>
              <img
                className={styles.discoveryDialogItemIconLmn}
                src={`${CDN_PATH}/images/step-one-green.svg`}
                alt="Step One"
              />
              <Box>
                <Typography variant="body3Bold" color="primary.main">
                  Select Sika at Checkout
                </Typography>
                <Typography variant="body4" color="gray1.main">
                  Choose Sika as your payment method to use your HSA/FSA at checkout.
                </Typography>
              </Box>

            </Box>

            <Box className={styles.discoveryDialogItemRowLmn}>
              <img
                className={styles.discoveryDialogItemIconLmn}
                src={`${CDN_PATH}/images/step-two-green.svg`}
                alt="Step Two"
              />
              <Box>
                <Typography variant="body3Bold" color="primary.main">
                  Get Your LMN & Make Your Purchase
                </Typography>
                <Typography variant="body4" color="gray1.main">
                  Complete a short survey to share details about your qualifying symptom(s) with Sika’s provider team.
                </Typography>
              </Box>
            </Box>
            <Box className={styles.discoveryDialogItemRowLmn}>
              <img
                className={styles.discoveryDialogItemIconLmn}
                src={`${CDN_PATH}/images/step-three-green.svg`}
                alt="Step Three"
              />
              <Box>
                <Typography variant="body3Bold" color="primary.main">
                  You&apos;re all set!
                </Typography>
                <Typography variant="body4" color="gray1.main">
                  Sika will send you an email with your receipt and the next steps for substantiation.
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </BaseDialog>
  )
}

export { DiscoveryLmnDialog, DiscoveryLmnDialogProps }
